/** Clé persist Zustand du thème / locale UI (voir `ui.store.ts`). */
const UI_STORAGE_KEY = 'ui-storage';

/**
 * Supprime les préférences de langue persistées pour retester la détection
 * (navigateur → repli anglais si langue non supportée).
 *
 * @param reload - Si `true` (défaut), recharge la page après nettoyage.
 */
export function clearLyrraLanguagePreference(options?: { reload?: boolean }): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem('app_language');
    localStorage.removeItem('locale');

    const raw = localStorage.getItem(UI_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { state?: Record<string, unknown> };
      if (parsed?.state && 'locale' in parsed.state) {
        const next = { ...parsed, state: { ...parsed.state } };
        delete next.state.locale;
        localStorage.setItem(UI_STORAGE_KEY, JSON.stringify(next));
      }
    }
  } catch (e) {
    console.warn('[Lyrra] clearLyrraLanguagePreference:', e);
  }

  const reload = options?.reload !== false;
  if (reload) {
    window.location.reload();
  }
}

function detectCurrentFlowIdFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const match = window.location.pathname.match(/\/eduflow\/flows\/([^/]+)/i);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/**
 * Vide le cache "texte simplifié" pour la formation affichée.
 * Disponible depuis la console navigateur : `clearSimplifiedTextCache()`
 */
export async function clearSimplifiedTextCacheForCurrentFlow(options?: {
  mode?: 'falc' | 'dys' | 'all';
  reload?: boolean;
}): Promise<void> {
  if (typeof window === 'undefined') return;
  const flowId = detectCurrentFlowIdFromUrl();
  if (!flowId) {
    console.warn('[Lyrra] clearSimplifiedTextCache: flowId introuvable dans l’URL.');
    return;
  }

  const mode = options?.mode ?? 'all';
  const endpoints = [
    `/api/eduflows/${encodeURIComponent(flowId)}/play/simplified-text/cache/clear`,
    `/api/eduflows/public/code/${encodeURIComponent(flowId)}/simplified-text/cache/clear`,
  ];
  let response: Response | null = null;
  for (const endpoint of endpoints) {
    const r = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ mode }),
    });
    if (r.ok || (r.status !== 404 && r.status !== 405)) {
      response = r;
      break;
    }
  }
  if (!response) {
    throw new Error('clearSimplifiedTextCache failed (no matching endpoint)');
  }

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    if (response.status === 401 || response.status === 403) {
      console.error(
        '[Lyrra] clearSimplifiedTextCache: accès refusé. Connectez-vous avec un compte autorisé sur cette formation.',
      );
      throw new Error(`clearSimplifiedTextCache unauthorized (${response.status})`);
    }
    console.error('[Lyrra] clearSimplifiedTextCache: échec', response.status, text);
    throw new Error(`clearSimplifiedTextCache failed (${response.status})`);
  }

  const payload = (await response.json().catch(() => null)) as
    | { data?: { deletedCount?: number; mode?: string; flowId?: string } }
    | null;
  const deletedCount = payload?.data?.deletedCount ?? 0;
  console.info(
    `[Lyrra] Cache texte simplifié vidé pour flow=${payload?.data?.flowId ?? flowId}, mode=${payload?.data?.mode ?? mode}, deleted=${deletedCount}.`,
  );

  if (options?.reload !== false) {
    window.location.reload();
  }
}

/** En dev uniquement : `clearLyrraLanguage()` dans la console du navigateur. */
export function exposeClearLanguageForDev(): void {
  if (!import.meta.env.DEV || typeof window === 'undefined') return;

  (
    window as unknown as {
      clearLyrraLanguage: (opts?: { reload?: boolean }) => void;
    }
  ).clearLyrraLanguage = clearLyrraLanguagePreference;

  console.info(
    '[Lyrra] Dev : tapez clearLyrraLanguage() pour effacer la langue enregistrée et recharger.',
  );
}

/** Expose la commande de purge cache simplifié en console (prod + dev). */
export function exposeSimplifiedTextCacheCommand(): void {
  if (typeof window === 'undefined') return;
  (
    window as unknown as {
      clearSimplifiedTextCache: (opts?: { mode?: 'falc' | 'dys' | 'all'; reload?: boolean }) => Promise<void>;
    }
  ).clearSimplifiedTextCache = clearSimplifiedTextCacheForCurrentFlow;

  console.info(
    '[Lyrra] Console : clearSimplifiedTextCache({ mode: \"all\" | \"falc\" | \"dys\", reload: true })',
  );
}
